interface CanonicalInput {
  host: string | null | undefined
  path: string
  // Set by a page whose request URL is not the form we advertise (e.g. a show
  // reached via a legacy `?date=` link instead of the dated path).
  declaredPath?: string | null
  robots?: string | null
  notFound?: boolean
  // False when SSRHead's failsafe fired before the route's prefetch landed, so
  // `notFound` and `declaredPath` can't be trusted yet.
  prefetchDone: boolean
}

// What ssr-render.js should do with an incoming guest URL.
export type SsrRouteAction =
  | { type: 'notFound' }
  | { type: 'redirect'; location: string }
  | { type: 'render' }

// Guest URLs need exactly one spelling per page. The routers are created without
// `strict`, so `/r/slug/` and `/r/slug` both render — the 301 in ssr-render.js and
// the canonical tag in SSRHead share this normalization so they can't disagree.
export function normalizePath(path: string): string {
  // Leading slashes collapse too: `//foo` is a second spelling of `/foo`, and a
  // Location header starting with `//` is protocol-relative — an open redirect to
  // whatever follows it.
  return path.replace(/^\/+/, '/').replace(/\/+$/, '') || '/'
}

// Split by hand rather than `new URL(url, base)`: the WHATWG parser throws on `//`
// and `//?x=1` (empty authority on a special scheme), and `//` matches the `/` route,
// so it reaches here on a request that used to render fine.
function splitUrl(originalUrl: string): { pathname: string; suffix: string } {
  const cut = originalUrl.search(/[?#]/)
  return cut === -1
    ? { pathname: originalUrl, suffix: '' }
    : { pathname: originalUrl.slice(0, cut), suffix: originalUrl.slice(cut) }
}

export function trailingSlashRedirect(originalUrl: string): string | null {
  const { pathname, suffix } = splitUrl(originalUrl)
  const normalized = normalizePath(pathname)
  if (normalized === pathname) {
    return null
  }
  return `${normalized}${suffix}`
}

// Ordering is the contract, and ssr-render.js and its spec both call this so they
// can't drift: an unmatched URL stays a single 404, a route-level redirect wins so
// /events/ reaches /calendar in one hop, and only then is a trailing slash stripped.
// `resolved` is typed structurally so this file stays free of vue-router.
export function resolveSsrRoute(
  originalUrl: string,
  resolved: { matched: { redirect?: unknown }[] },
): SsrRouteAction {
  if (resolved.matched.length === 0) {
    return { type: 'notFound' }
  }

  const redirect = resolved.matched[0]?.redirect
  if (typeof redirect === 'string') {
    return { type: 'redirect', location: `${redirect}${splitUrl(originalUrl).suffix}` }
  }

  const location = trailingSlashRedirect(originalUrl)
  return location ? { type: 'redirect', location } : { type: 'render' }
}

// The venue is resolved from req.hostname, so the canonical must name the same host:
// parsed, port stripped, lowercased. Express returns undefined when the request
// carried no Host header — the "unknown host" case canonicalUrl suppresses.
// The port strip is belt-and-braces (req.hostname carries none), so that reaching for
// the raw Host header here can't put a `:port` in a canonical URL. Anchored on a
// bracketed IPv6 literal or a run of non-colons so `[::1]:3000` keeps its address.
export function canonicalHost(hostname: string | null | undefined): string | null {
  return hostname ? hostname.toLowerCase().replace(/^(\[[^\]]*\]|[^:]*):\d+$/, '$1') : null
}

// Scheme is hardcoded to match the absolute URLs the backend already publishes in
// sitemap.xml and robots.txt; the ALB 301s http to https before SSR ever sees it.
export function canonicalUrl({
  host,
  path,
  declaredPath,
  robots,
  notFound,
  prefetchDone,
}: CanonicalInput): string | null {
  // Normalized here as well as at the ssr-render wiring, so a caller reaching for the raw
  // Host header can't put mixed case or a port in a canonical URL.
  const normalizedHost = canonicalHost(host)
  // Google ignores a canonical on a noindex page, and a 404 must not nominate itself.
  if (!normalizedHost || !prefetchDone || notFound || robots?.includes('noindex')) {
    return null
  }
  const pathname = (declaredPath || path).split(/[?#]/)[0]
  return `https://${normalizedHost}${normalizePath(pathname)}`
}
